import { FC, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { Modal } from "components/modals/Modal"
import { CRITERIA_LABELS_SHORT, SEVERITY_BADGE } from "components/risk/constants"
import { Column, DataTable } from "components/tables/DataTable"

import { riskService } from "services/risk/risk.service"
import { DuplicateMatch } from "services/risk/types"

interface Props {
  duplicates: DuplicateMatch[]
  currentUserId: string
  onReview: () => void
}

export const DuplicateMatchesTable: FC<Props> = ({ duplicates, currentUserId, onReview }) => {
  const [reviewing, setReviewing] = useState<string | null>(null)
  const [selected, setSelected] = useState<DuplicateMatch | null>(null)

  const handleReview = async (matchId: string, decision: string) => {
    setReviewing(matchId)
    try {
      await riskService.reviewDuplicate(currentUserId, matchId, decision)
      toast.success(`Match ${decision.toLowerCase()}`)
      setSelected(null)
      onReview()
    } finally {
      setReviewing(null)
    }
  }

  const columns = useMemo<Column<DuplicateMatch>[]>(
    () => [
      {
        header: "Matched Player",
        render: (dup) => (
          <div>
            <button
              className="link link-primary text-sm"
              onClick={(e) => {
                e.stopPropagation()
                window.open(`/user/${dup.other_player.id}`, "_blank")
              }}
            >
              {dup.other_player.username || dup.other_player.id}
            </button>
            {dup.other_player.email && <span className="block text-xs text-gray-400 dark:text-gray-500">{dup.other_player.email}</span>}
          </div>
        )
      },
      {
        header: "Similarity",
        render: (dup) => (
          <div className="flex items-center gap-2">
            <progress className="progress w-16" value={dup.similarity_score} max={100} />
            <span className="text-sm">{dup.similarity_score.toFixed(1)}%</span>
          </div>
        )
      },
      {
        header: "Severity",
        render: (dup) => (
          <span className={`badge badge-sm ${SEVERITY_BADGE[dup.severity] || "badge-ghost"}`}>{dup.severity.replace("_", " ")}</span>
        )
      },
      {
        header: "Status",
        render: (dup) => <span className="text-sm">{dup.review_status}</span>
      }
    ],
    []
  )

  return (
    <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-xl p-6">
      <h3 className="text-lg font-semibold mb-4">Potential Duplicates</h3>
      <DataTable rows={duplicates} columns={columns} rowKey={(d) => d.id} onRowClick={setSelected} />

      <Modal open={!!selected} handler={() => setSelected(null)}>
        {selected && (
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Duplicate Match — {selected.other_player.username || selected.other_player.id}</h3>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <progress className="progress w-20" value={selected.similarity_score} max={100} />
                <span className="text-sm font-medium">{selected.similarity_score.toFixed(1)}%</span>
              </div>
              <span className={`badge badge-sm ${SEVERITY_BADGE[selected.severity] || "badge-ghost"}`}>
                {selected.severity.replace("_", " ")}
              </span>
            </div>

            <h4 className="text-sm font-semibold mb-2">Criteria Scores</h4>
            <div className="grid grid-cols-5 gap-2 mb-6">
              {Object.entries(selected.criteria_scores).map(([key, score]) => (
                <div key={key} className="text-center">
                  <div className="text-xs mb-1 text-gray-500 dark:text-gray-400">{CRITERIA_LABELS_SHORT[key] || key}</div>
                  <div
                    className={`text-sm font-mono ${
                      score >= 70 ? "text-error" : score > 0 ? "text-warning" : "text-gray-300 dark:text-gray-600"
                    }`}
                  >
                    {score > 0 ? `${Math.round(score)}%` : "—"}
                  </div>
                </div>
              ))}
            </div>

            <div className="modal-action">
              {selected.review_status === "PENDING" ? (
                <>
                  <button className="btn btn-ghost" onClick={() => setSelected(null)}>
                    Close
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => handleReview(selected.id, "DISMISSED")}
                    disabled={reviewing === selected.id}
                  >
                    Dismiss
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => handleReview(selected.id, "CONFIRMED")}
                    disabled={reviewing === selected.id}
                  >
                    {reviewing === selected.id ? <span className="loading loading-spinner loading-xs" /> : "Confirm"}
                  </button>
                </>
              ) : (
                <>
                  <span className="text-sm text-gray-400 dark:text-gray-500">Already reviewed</span>
                  <button className="btn btn-ghost" onClick={() => setSelected(null)}>
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
